import { useParams } from "react-router-dom";
import { Box, ListItemText, useMediaQuery, useTheme } from "@mui/material"
import {
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

import { useMSALPayload } from "@initia-digital/auth-client-utils";
import { BasicLoader, CheckboxListItemButton, ConditionalLink, FilledButton, Typography } from "@initia-digital/component-library";

import { IPlanStageProps } from "../interfaces/componentInterfaces"
import { useWorkAndSafetyStore } from "../store";
import { useEquipmentClassList } from "../hooks";
import { PlanActions } from "./PlanActions";
import { ASSETBOTS, OrgSystem } from "@initia-digital/data-service-mapping";
import { EquipmentClassType, IEquipmentClass, IEquipmentClassUse } from "@initia-digital/types/work-and-safety";
import { useMemo, memo } from "react";
import { baseDefinedAndNotNullNarrowing, formatDisplayString, toSentenceCase } from "@initia-digital/ts-utils";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";


const EQUIPMENT_CLASS_TYPES = {
    INVESTIGATION: {
        type: 'INVESTIGATION' as EquipmentClassType,
        header: 'Investigation'
    },
    MONITORING: {
        type: 'MONITORING' as EquipmentClassType,
        header: 'Monitoring'
    },
    GEOLOCATION: {
        type: 'GEOLOCATION' as EquipmentClassType,
        header: 'Geolocation'
    },
    PPE_GENERAL: {
        type: 'PPE_GENERAL' as EquipmentClassType,
        header: 'Fundamental PPE'
    },
    PPE_SPECIFIC: {
        type: 'PPE_SPECIFIC' as EquipmentClassType,
        header: 'Special PPE'
    }
} as const;
const EQUIPMENT_CLASS_TYPES_SORT_ORDER = [
    EQUIPMENT_CLASS_TYPES.INVESTIGATION.type,
    EQUIPMENT_CLASS_TYPES.MONITORING.type,
    EQUIPMENT_CLASS_TYPES.GEOLOCATION.type,
    EQUIPMENT_CLASS_TYPES.PPE_GENERAL.type,
    EQUIPMENT_CLASS_TYPES.PPE_SPECIFIC.type
]

// ---------------------
interface EquipmentClassSectionProps {
    equipmentClassType: EquipmentClassType;
    equipmentClassTypeLabel: string;
    equipmentClassTypeList: IEquipmentClass[];
    jobId: string;
}

const EquipmentClassSection = memo(({
    equipmentClassType,
    equipmentClassTypeLabel,
    equipmentClassTypeList,
    jobId,
}: EquipmentClassSectionProps) => {
    // ---------------------
    const theme = useTheme();

    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();

    const { equipment } = editPlanState;

    const selectedEquipment = useMemo(() => equipment.filter(equipmentClassUse => equipmentClassUse.type === equipmentClassType), [equipment, equipmentClassType]);
    const selectedEquipmentNames = useMemo(() => selectedEquipment.map(equipmentClassUse => equipmentClassUse.name), [selectedEquipment]);
    const unselectedEquipment = useMemo(() => equipmentClassTypeList.filter(equipmentClass => !selectedEquipmentNames.includes(equipmentClass.name)), [equipmentClassTypeList, selectedEquipmentNames]);


    return (
        <Box key={`${equipmentClassType}-selection`}
            sx={{
                containerType: 'inline-size',
                containerName: `ppe-and-tools-section-${equipmentClassTypeLabel.split(' ').join('-')}`,
                width: '100%'
            }}
        >
            <Box
                display="flex" flexDirection="row" alignItems="center"
                mt="15px"
                width="100%"
            >
                <CheckboxListItemButton
                    isSelected={selectedEquipment.length === equipmentClassTypeList.length}
                    onSelect={() => {
                        // ---------------------
                        if (unselectedEquipment.length > 0) {
                            updateEditPlanState({
                                ...editPlanState,
                                equipment: [
                                    ...equipment,
                                    ...unselectedEquipment.map(equipmentClass => ({
                                        ...equipmentClass,
                                        equipmentClassId: equipmentClass.id,
                                        jobId,
                                        id: uuidv4()
                                    }))
                                ]
                            });
                        }
                        else {
                            // ---------------------
                            updateEditPlanState({
                                ...editPlanState,
                                equipment: equipment.filter((equipmentClass: IEquipmentClassUse) => equipmentClass.type !== equipmentClassType)
                            });
                        }
                    }}
                    label={
                        <ListItemText>
                            <Typography
                                fontSize={"0.9rem"}
                                fontWeight={700}
                                color={theme.palette.primary.contrastText}
                            >
                                {`All ${formatDisplayString(equipmentClassTypeLabel)}`}
                            </Typography>
                        </ListItemText>
                    }
                />
            </Box>
            <Box
                ml="20px"
                display="grid"
                gap="8px"
                sx={{
                    display: 'grid',
                    [`@container ppe-and-tools-section-${equipmentClassTypeLabel.split(' ').join('-')} (min-width: 900px)`]: {
                        gridTemplateColumns: '1fr 1fr 1fr'
                    },
                    [`@container ppe-and-tools-section-${equipmentClassTypeLabel.split(' ').join('-')} (max-width: 899px)`]: {
                        gridTemplateColumns: '1fr 1fr'
                    },
                    [`@container ppe-and-tools-section-${equipmentClassTypeLabel.split(' ').join('-')} (max-width: 549px)`]: {
                        gridTemplateColumns: '1fr'
                    }
                }}
            >
                {
                    equipmentClassTypeList.map((equipmentClass: IEquipmentClass) => (
                        <CheckboxListItemButton
                            key={`${equipmentClass.name}-checkbox-list-item-button`}
                            isSelected={selectedEquipmentNames.includes(equipmentClass.name)}
                            onSelect={() => {
                                // ---------------------
                                const equipmentUpdate = selectedEquipmentNames.includes(equipmentClass.name) ?
                                    [...equipment.filter(equipmentClassUse => equipmentClassUse.name !== equipmentClass.name)]
                                    :
                                    [...equipment, { ...equipmentClass, equipmentClassId: equipmentClass.id, jobId, id: uuidv4() }];

                                updateEditPlanState({
                                    ...editPlanState,
                                    equipment: equipmentUpdate
                                });
                            }}
                            label={
                                <ListItemText>
                                    <Box
                                        display="flex" flexDirection="column"
                                    >
                                        <Typography variant="body2" fontSize={"0.9rem"}>
                                            {formatDisplayString(equipmentClass.name)}
                                        </Typography>
                                        {
                                            baseDefinedAndNotNullNarrowing(equipmentClass.notes)
                                            && <Typography variant="body2" fontSize={"0.6rem"}>{formatDisplayString(equipmentClass.notes)}</Typography>
                                        }
                                    </Box>
                                </ListItemText>
                            }
                        />
                    ))
                }
            </Box>
        </Box>
    );
});

// ---------------------
export const PPEAndTools = ({
    handleSave, handleSaveAndNext,
    handleStatusChange
}: IPlanStageProps) => {
    // -----------------------------------------------------   

    const { editPlanState } = useWorkAndSafetyStore();
    const { title, status } = editPlanState;

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const params = useParams();
    const { planId, jobId } = params;

    const authDetail = useMSALPayload();

    // DATA INTEGRATION
    const { equipmentClassListLoading, equipmentClassListData } = useEquipmentClassList(authDetail, true);


    const equipmentListGroupedByType = useMemo(() => Object.groupBy(equipmentClassListData ?? [] as IEquipmentClass[], ({ type }) => type), [equipmentClassListData]);
    const sortedEquipmentClassTypes = useMemo(() => (
        (Object.keys(equipmentListGroupedByType) as EquipmentClassType[])
            .sort((a, b) => {
                const indexA = EQUIPMENT_CLASS_TYPES_SORT_ORDER.indexOf(a);
                const indexB = EQUIPMENT_CLASS_TYPES_SORT_ORDER.indexOf(b);

                // If both items are not in the sort order, maintain their relative order
                if (indexA === -1 && indexB === -1) return 0;
                // If a is not in sort order, move it to the end
                if (indexA === -1) return 1;
                // If b is not in sort order, move it to the end
                if (indexB === -1) return -1;
                // Normal sort for items in the sort order
                return indexA - indexB;
            })
    ), [equipmentListGroupedByType]);


    return (
        <Box
            sx={{
                width: '100%',
                containerType: 'inline-size'
            }}
        >
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection={isSmallerThanSm ? "column" : "row"} flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="PPE and Tools" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>
            <Box display="flex" flexDirection="column" mt="10px" mb="20px">
                <Box>
                    <Typography
                        fontSize={"0.7rem"}
                        fontWeight={300}
                        color={theme.palette.primary.contrastText}
                    >
                        You can visit Assetbots to book equipment
                    </Typography>
                </Box>
                <Box
                    key={`ppe-and-tools-assetbots-link`}
                    display="flex" flexDirection="row"
                >
                    <ConditionalLink
                        href={OrgSystem.constructSystemPath(ASSETBOTS, 'allFieldGear', {})}
                        newTab={true}
                    >
                        <FilledButton
                            backgroundColor={ASSETBOTS.bgColor}
                            color={ASSETBOTS.color}
                            size={isSmallerThanMd ? "small" : "medium"}
                        >
                            {ASSETBOTS.name}
                        </FilledButton>
                    </ConditionalLink>
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" mt="10px" mb="25px">
                <Box>
                    <Typography
                        fontSize={"0.7rem"}
                        fontWeight={300}
                        color={theme.palette.primary.contrastText}
                    >
                        Specify the PPE and tools required for this work
                    </Typography>
                </Box>
                {
                    equipmentClassListLoading && (
                        <BasicLoader
                            size={50}
                        />
                    )
                }
                {
                    !equipmentClassListLoading && baseDefinedAndNotNullNarrowing(equipmentClassListData) &&
                    sortedEquipmentClassTypes.map(equipmentClassType => {
                        const equipmentClassTypeLabel = EQUIPMENT_CLASS_TYPES?.[equipmentClassType]?.header ?? toSentenceCase(equipmentClassType);
                        const equipmentClassList = equipmentListGroupedByType[equipmentClassType] ?? [];
                        return (
                            <EquipmentClassSection
                                key={`${equipmentClassType}-section`}
                                equipmentClassType={equipmentClassType}
                                equipmentClassTypeLabel={equipmentClassTypeLabel}
                                equipmentClassTypeList={equipmentClassList}
                                jobId={jobId ?? ''}
                            />
                        );
                    })
                }
            </Box>
            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}